import { UserRole } from "@/features/auth";
import { ApiDomain, ROLE_BY_API_DOMAIN } from "@/shared/utils";

/**
 * Single source of truth for the realtime layer: which RSocket topics exist,
 * how to build their full names per apiDomain, which roles may subscribe to
 * which topic, and how to derive the topics for a given domain.
 *
 * Two pieces of state, type-pinned together:
 * - RSOCKET_TOPICS — the topic-string factories
 * - REALTIME_TOPIC_ROLES — per topic-key the roles allowed to subscribe
 *
 * Updating rules: change REALTIME_TOPIC_ROLES *and* docs/AUTH_MATRIX.md *and*
 * the expected set in the test for this file. The three are kept in sync by
 * code review + test, not automation.
 */

/**
 * Factory functions for every realtime topic the backend publishes. Each entry
 * builds the full topic string (`{apiDomain}.{path}`) for a given user domain.
 */
export const RSOCKET_TOPICS = {
    acties: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.acties` as const,
    berichten: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.berichten` as const,
    contactpersonen: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.contactpersonen` as const,
    betrokkenStations: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.betrokken-stations` as const,
    betrokkenTreinen: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.betrokken-treinen` as const,
    dossierDetails: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.details` as const,
    dossierOverzicht: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.overzicht` as const,
    dossierSettings: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.settings` as const,
    logboek: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.logboek` as const,
    medewerkersOpStation: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.medewerkers-op-stations` as const,
    ongewensteGebeurtenis: (apiDomain: ApiDomain) => `${apiDomain}.ongewenste-gebeurtenis` as const,
    opstartplan: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.opstartplan` as const,
    suggesties: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.suggesties` as const,
    vestigingen: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.vestigingen` as const,
    landelijkeFaciliteiten: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.landelijke-faciliteiten` as const,
    vsm: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.vsm` as const,
    tvv: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.tvv` as const,
    herinneringen: (apiDomain: ApiDomain) => `${apiDomain}.dossiers.herinneringen` as const,
} as const;

/** Union of every topic-string the realtime layer can produce. */
export type RSocketTopic = ReturnType<(typeof RSOCKET_TOPICS)[keyof typeof RSOCKET_TOPICS]>;

// Role-bundles, named by intent so the table reads aloud.
const DOSSIER_VIEWER_ROLES = [
    UserRole.REGIEVOERDER,
    UserRole.SUPPORTCONSULTANT,
    UserRole.KLANTENSERVICE,
    UserRole.TRANSVISION,
    UserRole.DEWIT,
    UserRole.ALLEENLEZEN,
] as const satisfies readonly UserRole[];

const REGIEVOERDER_ONLY = [UserRole.REGIEVOERDER] as const satisfies readonly UserRole[];

/**
 * Per realtime topic, the roles allowed to subscribe.
 */
export const REALTIME_TOPIC_ROLES = {
    acties: [UserRole.REGIEVOERDER, UserRole.KLANTENSERVICE, UserRole.TRANSVISION, UserRole.DEWIT],
    berichten: REGIEVOERDER_ONLY,
    contactpersonen: [UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN],
    betrokkenStations: DOSSIER_VIEWER_ROLES,
    betrokkenTreinen: DOSSIER_VIEWER_ROLES,
    dossierDetails: DOSSIER_VIEWER_ROLES,
    dossierSettings: DOSSIER_VIEWER_ROLES,
    dossierOverzicht: DOSSIER_VIEWER_ROLES,
    logboek: DOSSIER_VIEWER_ROLES,
    medewerkersOpStation: [UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT],
    ongewensteGebeurtenis: REGIEVOERDER_ONLY,
    opstartplan: DOSSIER_VIEWER_ROLES,
    suggesties: REGIEVOERDER_ONLY,
    vestigingen: [UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.KLANTENSERVICE, UserRole.ALLEENLEZEN],
    landelijkeFaciliteiten: [UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.KLANTENSERVICE, UserRole.ALLEENLEZEN],
    vsm: DOSSIER_VIEWER_ROLES,
    tvv: DOSSIER_VIEWER_ROLES,
    herinneringen: [UserRole.REGIEVOERDER],
} as const satisfies Record<keyof typeof RSOCKET_TOPICS, readonly UserRole[]>;

export type TopicKey = keyof typeof REALTIME_TOPIC_ROLES;

/**
 * Returns every concrete topic string the given apiDomain may subscribe to,
 * derived from REALTIME_TOPIC_ROLES. Used by the WebSocket debug terminal.
 */
export function topicsForDomain(apiDomain: ApiDomain): RSocketTopic[] {
    const role = ROLE_BY_API_DOMAIN[apiDomain];
    return (Object.keys(REALTIME_TOPIC_ROLES) as TopicKey[])
        .filter((key) => (REALTIME_TOPIC_ROLES[key] as readonly UserRole[]).includes(role))
        .map((key) => RSOCKET_TOPICS[key](apiDomain));
}
